/**
 * CallHistory — timeline of every call for a candidate.
 *
 * Rendered inside CandidateProfile. Each row shows direction, date,
 * duration, an audio player if a recording exists, plus the Deepgram
 * transcription and the Claude summary once the async pipeline finishes.
 *
 * Poll frequency is bumped while transcription is pending so the user sees
 * the summary appear without a manual refresh.
 */
import { motion } from "framer-motion";
import {
    AlertTriangle,
    Loader2,
    PhoneIncoming,
    PhoneOutgoing,
    RefreshCw,
    Sparkles,
    Trash2,
    Voicemail,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { api, API_BASE, tokenStore } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { hasAnyRole } from "../../lib/roles";
import ConfirmModal from "../common/ConfirmModal";

const STATUS_LABEL = {
    completed: "Terminé",
    "no-answer": "Pas de réponse",
    busy: "Occupé",
    failed: "Échec",
    canceled: "Annulé",
    voicemail: "Messagerie",
    ringing: "Sonnerie",
    "in-progress": "En cours",
    queued: "En attente",
};

const SENTIMENT_META = {
    positive: { label: "Positif", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    neutral: { label: "Neutre", tone: "bg-slate-50 text-slate-600 border-slate-200" },
    negative: { label: "Vigilance", tone: "bg-amber-50 text-amber-800 border-amber-200" },
};

function DirectionIcon({ direction, voicemail }) {
    if (voicemail) return <Voicemail className="h-4 w-4 text-amber-600" />;
    if (direction === "inbound") return <PhoneIncoming className="h-4 w-4 text-emerald-600" />;
    return <PhoneOutgoing className="h-4 w-4 text-shariti-orange" />;
}

function formatDuration(sec) {
    if (!Number.isFinite(sec) || sec <= 0) return "—";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${String(s).padStart(2, "0")}s`;
}

/**
 * Authenticated audio playback — we can't put the Bearer token in an
 * `<audio src>` because the browser won't inject it. Instead we fetch
 * the MP3 with axios (which adds the header), turn it into a blob URL,
 * and hand that to <audio>. Blob is revoked on unmount to avoid leaks.
 */
function AuthedAudio({ callLogId }) {
    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const urlRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const token = tokenStore.get();
                const res = await fetch(`${API_BASE}/twilio/calls/${callLogId}/recording`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                urlRef.current = url;
                setSrc(url);
            } catch (e) {
                setError(e.message || "Impossible de charger l'enregistrement");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (urlRef.current) {
                URL.revokeObjectURL(urlRef.current);
                urlRef.current = null;
            }
        };
    }, [callLogId]);

    if (loading) return <div className="text-xs text-slate-400 italic">Chargement de l&apos;enregistrement…</div>;
    if (error) return <div className="text-xs text-shariti-red">{error}</div>;
    return (
        <audio
            data-testid={`call-history-audio-${callLogId}`}
            controls
            src={src}
            className="w-full mt-2 rounded-lg"
            preload="metadata"
        />
    );
}

function CallRow({ call, isAdmin, onRequestDelete, onReprocess, deletingId }) {
    const [expanded, setExpanded] = useState(false);
    const [busy, setBusy] = useState(null);
    const sentiment = SENTIMENT_META[call.ai_sentiment] || null;
    const hasTranscript = !!call.transcript;
    const hasSummary = !!call.ai_summary;
    const pending = call.recording_url && !hasTranscript;
    const isDeleting = deletingId === call.id;

    const handleDelete = (e) => {
        e.stopPropagation();
        onRequestDelete?.(call);
    };
    const handleReprocess = async (e) => {
        e.stopPropagation();
        setBusy("reprocess");
        try { await onReprocess?.(call.id); } finally { setBusy(null); }
    };

    return (
        <div
            data-testid={`call-history-row-${call.id}`}
            className="rounded-xl border border-slate-200 bg-white p-3 hover:border-shariti-orange/40 transition"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-shariti-cream flex items-center justify-center shrink-0">
                        <DirectionIcon direction={call.direction} voicemail={call.voicemail} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs font-extrabold text-shariti-black">
                            {call.voicemail ? "Message vocal" : call.direction === "inbound" ? "Appel entrant" : "Appel sortant"}
                            <span className="ml-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                · {STATUS_LABEL[call.status] || call.status}
                            </span>
                        </div>
                        <div className="text-xs text-slate-600 font-semibold truncate">
                            {call.user_name && <>Par <strong>{call.user_name}</strong> · </>}
                            {formatDateTime(call.created_at)}
                            <span className="text-slate-400"> · {formatDuration(call.duration_sec || call.recording_duration_sec)}</span>
                        </div>
                    </div>
                </div>
                {sentiment && (
                    <span className={`shrink-0 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${sentiment.tone}`}>
                        {sentiment.label}
                    </span>
                )}
                {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                        {call.recording_url && !hasSummary && (
                            <button
                                type="button"
                                onClick={handleReprocess}
                                disabled={busy === "reprocess"}
                                data-testid={`call-history-reprocess-${call.id}`}
                                title="Retraiter (transcription + résumé IA)"
                                className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-slate-100 hover:bg-amber-100 text-slate-500 hover:text-amber-700 transition disabled:opacity-40"
                            >
                                {busy === "reprocess" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            data-testid={`call-history-delete-${call.id}`}
                            title="Supprimer cet appel (admin)"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-700 transition disabled:opacity-40"
                        >
                            {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        </button>
                    </div>
                )}
            </div>

            {call.recording_url && <AuthedAudio callLogId={call.id} />}

            {pending && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Transcription et résumé IA en cours…
                </div>
            )}

            {hasSummary && (
                <div className="mt-2 rounded-lg bg-shariti-orange-soft/40 border border-shariti-orange/20 p-2.5">
                    <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                        <Sparkles className="h-3 w-3" /> Résumé IA
                    </div>
                    <div className="text-xs text-shariti-black leading-relaxed">{call.ai_summary}</div>
                    {(call.ai_key_points?.length || 0) > 0 && (
                        <div className="mt-2">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Points clés</div>
                            <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                                {call.ai_key_points.slice(0, 5).map((k, i) => (<li key={i}>{k}</li>))}
                            </ul>
                        </div>
                    )}
                    {(call.ai_next_actions?.length || 0) > 0 && (
                        <div className="mt-2">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Actions</div>
                            <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                                {call.ai_next_actions.slice(0, 5).map((k, i) => (<li key={i}>{k}</li>))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {hasTranscript && (
                <div className="mt-2">
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`call-history-toggle-transcript-${call.id}`}
                        className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-shariti-orange"
                    >
                        {expanded ? "Masquer la transcription" : "Voir la transcription"}
                    </button>
                    {expanded && (
                        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-700 leading-relaxed max-h-64 overflow-y-auto whitespace-pre-line">
                            {call.transcript}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function CallHistory({ candidateId }) {
    const { user } = useAuth();
    const isAdmin = user && hasAnyRole(user, "admin");
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const pending = useMemo(() => calls.some((c) => c.recording_url && !c.transcript), [calls]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/twilio/calls", { params: { candidate_id: candidateId } });
            setCalls(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Impossible de charger l'historique");
        } finally {
            setLoading(false);
        }
    }, [candidateId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const interval = pending ? 5000 : 30000;
        const id = setInterval(load, interval);
        return () => clearInterval(id);
    }, [pending, load]);

    const requestDelete = useCallback((call) => {
        setPendingDelete(call);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        const id = pendingDelete.id;
        setDeletingId(id);
        try {
            await api.delete(`/twilio/calls/${id}`);
            setCalls((cs) => cs.filter((c) => c.id !== id));
            toast.success("Appel supprimé");
            setPendingDelete(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Suppression impossible");
        } finally {
            setDeletingId(null);
        }
    }, [pendingDelete]);

    const onReprocess = useCallback(async (id) => {
        try {
            await api.post(`/twilio/calls/${id}/reprocess`);
            toast.info("Retraitement lancé — le résumé IA arrive dans ~20 s");
            for (let i = 0; i < 6; i += 1) {
                await new Promise((r) => setTimeout(r, 5000));
                await load();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Retraitement impossible");
        }
    }, [load]);

    if (loading) {
        return (
            <div className="text-xs text-slate-400 italic inline-flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" /> Chargement…
            </div>
        );
    }
    if (error) return <div className="text-xs text-shariti-red">{error}</div>;
    if (calls.length === 0) {
        return (
            <div className="text-xs text-slate-500 italic">
                Aucun appel enregistré pour ce candidat.
            </div>
        );
    }
    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="call-history-list"
                className="space-y-2"
            >
                {calls.map((call) => (<CallRow key={call.id} call={call} isAdmin={isAdmin} onRequestDelete={requestDelete} onReprocess={onReprocess} deletingId={deletingId} />))}
            </motion.div>

            {pendingDelete && (
                <ConfirmModal
                    testid="call-history-delete-modal"
                    tone="red"
                    icon={AlertTriangle}
                    title="Supprimer cet appel ?"
                    body={
                        <div className="space-y-2">
                            <p>
                                Cette action est <strong>définitive</strong>. L&apos;enregistrement audio hébergé chez
                                Twilio sera lui aussi supprimé.
                            </p>
                            <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs">
                                <div className="font-black text-shariti-black">
                                    {pendingDelete.direction === "inbound" ? "Appel entrant" : "Appel sortant"}
                                    {pendingDelete.voicemail ? " · Messagerie" : ""}
                                </div>
                                <div className="text-slate-500 mt-0.5">
                                    {formatDateTime(pendingDelete.created_at)}
                                    {" · "}
                                    {formatDuration(pendingDelete.duration_sec || pendingDelete.recording_duration_sec)}
                                </div>
                            </div>
                        </div>
                    }
                    confirmLabel={deletingId ? "Suppression…" : "Supprimer l'appel"}
                    cancelLabel="Annuler"
                    onConfirm={deletingId ? () => {} : confirmDelete}
                    onCancel={deletingId ? () => {} : () => setPendingDelete(null)}
                />
            )}
        </>
    );
}
